/* BiologicalAgePanel — Levine PhenoAge calculator (Clinic+ plan or 2 credits) */
import React, { useState, useEffect, useMemo } from "react";
import AIModuleNotice from "@/components/AIModuleNotice";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";
import PanelCostBadge from "@/components/pricing/PanelCostBadge";
import BioAgeTrendChart from "@/components/BioAgeTrendChart";
import { ComplianceDisclaimer, AIBadge, EvidenceBadge } from "@/components/compliance/ComplianceDisclaimer";
import SaveToHealthIntelligenceButton from "@/components/SaveToHealthIntelligenceButton";
import { Hourglass, ChevronRight, Sparkles, Zap } from "lucide-react";
import WhyThisOutput from "@/components/WhyThisOutput";
import ScientificSourcesPopover from "@/components/scientific/ScientificSourcesPopover";
import { BIOLOGICAL_AGE_SOURCES } from "@/data/scientificSources";

// 9 biomarkers + age. Fields with `units` array allow the user to toggle
// between US (required by Levine formula) and SI (common in EU labs).
// `conv` functions convert FROM the currently displayed unit TO the US baseline.
const FIELDS = [
  { id: "age", bg: "Хронологична възраст", en: "Chronological age", unit: "години", min: 18, max: 110, step: 1 },
  { id: "albumin_g_dl", bg: "Албумин", en: "Albumin", min: 1.5, max: 6.5, step: 0.1, ref: "3.5–5.0",
    units: [
      { code: "g/dL", label: "g/dL", to_us: v => v },
      { code: "g/L",  label: "g/L",  to_us: v => v / 10 },
    ] },
  { id: "creatinine_mg_dl", bg: "Креатинин", en: "Creatinine", min: 0.2, max: 15.0, step: 0.01, ref: "0.6–1.2",
    units: [
      { code: "mg/dL",  label: "mg/dL",  to_us: v => v },
      { code: "µmol/L", label: "μmol/L", to_us: v => v / 88.4017 },
    ] },
  { id: "glucose_mg_dl", bg: "Кръвна захар (гладно)", en: "Fasting glucose", min: 40, max: 600, step: 1, ref: "70–100",
    units: [
      { code: "mg/dL",  label: "mg/dL",  to_us: v => v },
      { code: "mmol/L", label: "mmol/L", to_us: v => v * 18.0182 },
    ] },
  { id: "crp_mg_dl", bg: "CRP", en: "CRP", min: 0, max: 30, step: 0.01, ref: "<0.5",
    units: [
      { code: "mg/dL", label: "mg/dL", to_us: v => v },
      { code: "mg/L",  label: "mg/L",  to_us: v => v / 10 },
    ] },
  { id: "lymph_pct", bg: "Лимфоцити %", en: "Lymphocyte %", unit: "%", min: 1, max: 80, step: 0.1, ref: "20–40" },
  { id: "mcv_fl", bg: "MCV", en: "MCV", unit: "fL", min: 60, max: 130, step: 0.1, ref: "80–100" },
  { id: "rdw_pct", bg: "RDW", en: "RDW", unit: "%", min: 9, max: 30, step: 0.1, ref: "11.5–14.5" },
  { id: "alp_u_l", bg: "ALP (алкална фосфатаза)", en: "ALP", unit: "U/L", min: 20, max: 600, step: 1, ref: "40–130" },
  { id: "wbc_k_ul", bg: "Левкоцити (WBC)", en: "WBC", unit: "10³/µL", min: 1, max: 50, step: 0.1, ref: "4.0–10.0" },
];

const PACE_STYLE = {
  much_younger: { bg: "Много по-млад", en: "Much younger", css: "text-emerald-700 bg-emerald-100" },
  younger:      { bg: "По-млад",        en: "Younger",       css: "text-emerald-700 bg-emerald-50" },
  on_track:     { bg: "В норма",        en: "On track",      css: "text-sky-700 bg-sky-50" },
  older:        { bg: "По-стар",        en: "Older",         css: "text-amber-700 bg-amber-50" },
  much_older:   { bg: "Много по-стар", en: "Much older",    css: "text-rose-700 bg-rose-100" },
};

function ResultCard({ result, bg }) {
  const pace = PACE_STYLE[result.pace_of_aging] || PACE_STYLE.on_track;
  const gapPositive = result.age_gap > 0;
  return (
    <div className="space-y-3" data-testid="phenoage-result">
      {/* Headline: chronological vs biological */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">{bg ? "Хронологична" : "Chronological"}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{result.chronological_age}</div>
          <div className="text-[10px] text-gray-400">{bg ? "години" : "years"}</div>
        </div>
        <div className={`rounded-lg border p-3 text-center ${gapPositive ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
          <div className="text-[10px] uppercase tracking-wide text-gray-600">{bg ? "Биологична" : "Biological"}</div>
          <div className={`text-2xl font-bold mt-1 ${gapPositive ? "text-rose-700" : "text-emerald-700"}`}>{result.biological_age}</div>
          <div className="text-[10px] text-gray-500">{bg ? "години" : "years"}</div>
        </div>
      </div>

      {/* Age gap + pace pill */}
      <div className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2.5">
        <div>
          <div className="text-[10px] uppercase text-gray-500">{bg ? "Разлика" : "Age gap"}</div>
          <div className={`text-lg font-semibold ${gapPositive ? "text-rose-700" : "text-emerald-700"}`}>
            {gapPositive ? "+" : ""}{result.age_gap} {bg ? "години" : "yrs"}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${pace.css}`} data-testid={`phenoage-pace-${result.pace_of_aging}`}>
          <Sparkles size={11} />{bg ? pace.bg : pace.en}
        </span>
      </div>

      {/* 10-year mortality score (informational) */}
      <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 text-[11px] text-gray-600">
        <div className="flex items-center justify-between">
          <span>{bg ? "10-год. mortality risk (Gompertz)" : "10-year mortality risk (Gompertz)"}</span>
          <span className="font-mono font-semibold text-gray-900">{(result.mortality_score * 100).toFixed(2)}%</span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full ${result.mortality_score > 0.15 ? "bg-rose-500" : result.mortality_score > 0.05 ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${Math.min(100, result.mortality_score * 100 * 3)}%` }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <AIBadge /><EvidenceBadge />
      </div>
      <div className="text-[10px] text-gray-400 italic leading-relaxed">
        {bg
          ? "Формула: Levine ME et al., J Gerontol A Biol Sci Med Sci. 2018;73(7):939-945. Валидирано върху 11 432 NHANES участници."
          : "Formula: Levine ME et al., J Gerontol A Biol Sci Med Sci. 2018;73(7):939-945. Validated on 11,432 NHANES participants."}
      </div>
    </div>
  );
}

export default function BiologicalAgePanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [values, setValues] = useState({});
  const [unitChoice, setUnitChoice] = useState({});  // { albumin_g_dl: "g/L", ... }
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [autofilling, setAutofilling] = useState(false);
  const [autofillMeta, setAutofillMeta] = useState(null);  // {source_filename, source_date, missing_fields}

  // Load history on mount
  useEffect(() => {
    axios.get(`${API}/biological-age/history?limit=5`)
      .then(({ data }) => setHistory(data.history || []))
      .catch(() => setHistory([]));
  }, [API]);

  const allFilled = useMemo(() =>
    FIELDS.every(f => values[f.id] !== undefined && values[f.id] !== "" && !isNaN(Number(values[f.id]))),
    [values]);

  const handleChange = (id, v) => setValues(prev => ({ ...prev, [id]: v }));

  const handleAutofill = async () => {
    setAutofilling(true);
    try {
      const { data } = await axios.get(`${API}/biological-age/from-latest-lab`);
      const patch = { ...(data.markers || {}) };
      if (data.age) patch.age = data.age;
      setValues(prev => ({ ...prev, ...patch }));
      setAutofillMeta({
        source_filename: data.source_filename,
        source_date: data.source_date,
        missing_fields: data.missing_fields || [],
        extracted_count: Object.keys(data.markers || {}).length,
      });
      const missing = (data.missing_fields || []).length;
      if (missing === 0 && data.age) {
        toast.success(bg ? "Всички маркери попълнени от лабораторията!" : "All markers auto-filled from lab!");
      } else {
        toast.info(bg
          ? `Попълнени ${Object.keys(data.markers || {}).length} маркера. ${missing ? `Липсват: ${missing}` : ""}${!data.age ? " · Възраст — липсва в профила" : ""}`
          : `${Object.keys(data.markers || {}).length} markers filled. ${missing ? `Missing: ${missing}` : ""}${!data.age ? " · Age missing in profile" : ""}`,
          { duration: 6000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Няма намерени лабораторни резултати" : "No lab results found"));
    } finally { setAutofilling(false); }
  };

  const handleSubmit = async () => {
    if (!allFilled) { toast.error(bg ? "Попълнете всички полета" : "Fill all fields"); return; }
    setLoading(true);
    try {
      // Apply unit conversion per field — convert displayed value → US baseline for the API
      const payload = {};
      for (const f of FIELDS) {
        const raw = Number(values[f.id]);
        if (f.units) {
          const sel = unitChoice[f.id] || f.units[0].code;
          const unit = f.units.find(u => u.code === sel) || f.units[0];
          payload[f.id] = Number(unit.to_us(raw).toFixed(4));
        } else {
          payload[f.id] = raw;
        }
      }
      const { data } = await axios.post(`${API}/biological-age/calculate`, { ...payload, save: true });
      setResult(data);
      toast.success(bg ? "Изчислено!" : "Calculated!");
      const { data: h } = await axios.get(`${API}/biological-age/history?limit=5`);
      setHistory(h.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при изчисление" : "Calculation error"));
    } finally { setLoading(false); }
  };

  const handleReset = () => { setResult(null); };

  return (
    <CollapsiblePanel
      title={bg ? "Биологична възраст" : "Biological Age"}
      subtitle={bg ? "Levine PhenoAge" : "Levine PhenoAge"}
      icon={Hourglass}
      iconColor="#8B5CF6"
      credits={null}
      badge={null}
      storageKey="biological-age"
      testId="biological-age-panel"
      defaultOpen={false}
    >
      {result ? (
        <div className="space-y-4">
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <ScientificSourcesPopover
              sources={BIOLOGICAL_AGE_SOURCES}
              testIdPrefix="bio-age-sources"
            />
            <WhyThisOutput
              model={bg ? "Levine PhenoAge (peer-reviewed формула)" : "Levine PhenoAge (peer-reviewed formula)"}
              inputs={bg
                ? ["9 биомаркера (albumin, creatinine, glucose, CRP, lymphocytes, MCV, RDW, alkaline phosphatase, WBC)", "Възраст"]
                : ["9 biomarkers (albumin, creatinine, glucose, CRP, lymphocytes, MCV, RDW, alkaline phosphatase, WBC)", "Age"]}
              method={bg
                ? "Детерминистична формула на Levine et al. 2018 (Aging). НЕ е AI prediction — математическо изчисление. Не е диагноза."
                : "Deterministic Levine et al. 2018 (Aging) formula. NOT an AI prediction — mathematical calculation. Not a diagnosis."}
              docHref="/legal#ai-methodology"
              dataTestid="bio-age-why-this"
            />
          </div>
          <ResultCard result={result} bg={bg} />

          {/* iter425 — Save to Health Intelligence */}
          <div className="flex justify-end">
            <SaveToHealthIntelligenceButton
              snapshotType="lab_panel"
              title={bg
                ? `Биологична възраст · ${result.biological_age || "—"}г (хрон. ${result.chronological_age || "—"})`
                : `Biological Age · ${result.biological_age || "—"}y (chrono ${result.chronological_age || "—"})`}
              summary={bg
                ? `PhenoAge (Levine 2018). Разлика: ${(result.biological_age != null && result.chronological_age != null) ? ((result.biological_age - result.chronological_age) >= 0 ? "+" : "") + (result.biological_age - result.chronological_age).toFixed(1) : "—"}г`
                : `PhenoAge (Levine 2018). Gap: ${(result.biological_age != null && result.chronological_age != null) ? ((result.biological_age - result.chronological_age) >= 0 ? "+" : "") + (result.biological_age - result.chronological_age).toFixed(1) : "—"}y`}
              payload={{
                biological_age: result.biological_age,
                chronological_age: result.chronological_age,
                biomarkers: result.biomarkers,
                mortality_risk: result.mortality_risk,
                computed_at: new Date().toISOString(),
              }}
              tags={["phenoage", "Levine-2018", "biological-age"]}
              size="sm"
              testId="bio-age-save-to-hi-btn"
            />
          </div>

          <button
            onClick={handleReset}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
            data-testid="phenoage-reset-btn"
          >
            {bg ? "Ново изчисление" : "New calculation"}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <ComplianceDisclaimer context="biological_age" />

          {/* One-click autofill from the latest lab analysis */}
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 flex items-start gap-3" data-testid="phenoage-autofill-section">
            <div className="shrink-0 rounded-md bg-violet-100 p-1.5">
              <Zap size={16} className="text-violet-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-violet-900">
                {bg ? "Попълни автоматично от лабораторни резултати" : "Auto-fill from lab results"}
              </div>
              <div className="text-[11px] text-violet-800 mt-0.5">
                {bg
                  ? "Извличаме 9-те маркера от последния качен PDF и автоматично конвертираме единиците (SI → US)."
                  : "We extract the 9 markers from your latest uploaded PDF and auto-convert units (SI → US)."}
              </div>
              {autofillMeta && (
                <div className="mt-2 text-[11px] text-violet-700" data-testid="phenoage-autofill-meta">
                  <div>📄 {autofillMeta.source_filename?.slice(0, 60) || "—"}{autofillMeta.source_filename?.length > 60 ? "…" : ""}</div>
                  <div>✓ {autofillMeta.extracted_count} {bg ? "маркера попълнени" : "markers filled"}
                    {autofillMeta.missing_fields?.length > 0 && (
                      <span className="text-rose-700"> · {bg ? "Липсват" : "Missing"}: {autofillMeta.missing_fields.length}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={handleAutofill}
              disabled={autofilling}
              className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                autofilling ? "bg-gray-300 text-gray-500" : "bg-violet-600 text-white hover:bg-violet-700"
              }`}
              data-testid="phenoage-autofill-btn"
            >
              {autofilling ? (bg ? "Извличане…" : "Extracting…") : (bg ? "Попълни" : "Auto-fill")}
            </button>
          </div>

          <p className="text-xs text-gray-600 leading-snug">
            {bg
              ? 'Въведете 9 кръвни маркера + вашата възраст. Резултатът показва вашата „биологична възраст" спрямо хронологичната — отразява цялостното метаболитно и имунологично състояние.'
              : 'Enter 9 blood markers + your age. The result shows your "biological age" vs chronological — reflecting overall metabolic and immune state.'}
          </p>

          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(f => {
              const isMissing = autofillMeta?.missing_fields?.includes(f.id);
              const wasAutofilled = autofillMeta && !isMissing && values[f.id] !== undefined && values[f.id] !== "";
              const selectedUnit = f.units ? (unitChoice[f.id] || f.units[0].code) : f.unit;
              return (
                <label key={f.id} className="block" data-testid={`phenoage-field-${f.id}`}>
                  <span className="flex items-center gap-1 text-[11px] font-medium text-gray-700 mb-0.5">
                    <span>{bg ? f.bg : f.en}</span>
                    {f.units ? (
                      <select
                        value={selectedUnit}
                        onChange={(e) => setUnitChoice(prev => ({ ...prev, [f.id]: e.target.value }))}
                        className="ml-1 border border-gray-300 rounded px-1 py-0 text-[10px] bg-white text-gray-600 focus:border-violet-500 focus:ring-0"
                        data-testid={`phenoage-unit-${f.id}`}
                        aria-label={bg ? `Мерна единица за ${f.bg}` : `Unit for ${f.en}`}
                      >
                        {f.units.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
                      </select>
                    ) : (
                      <span className="text-gray-400 font-normal">({f.unit})</span>
                    )}
                    {wasAutofilled && <span className="text-emerald-600 text-[10px]" title={bg ? "От лаб. резултати" : "From lab"}>✓</span>}
                    {isMissing && <span className="text-rose-600 text-[10px]" title={bg ? "Липсва в лабораторията" : "Missing from lab"}>!</span>}
                  </span>
                  <input
                    type="number"
                    value={values[f.id] ?? ""}
                    onChange={(e) => handleChange(f.id, e.target.value)}
                    placeholder={f.ref || ""}
                    className={`w-full rounded-md border px-2.5 py-1.5 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500 ${
                      isMissing ? "border-rose-300 bg-rose-50" : wasAutofilled ? "border-emerald-300 bg-emerald-50" : "border-gray-300"
                    }`}
                    data-testid={`phenoage-input-${f.id}`}
                  />
                  {f.ref && (
                    <span className="text-[10px] text-gray-400">
                      {bg ? "реф: " : "ref: "}{f.ref}
                      {f.units && selectedUnit !== f.units[0].code && (
                        <span className="ml-1 italic text-violet-600">· {bg ? "ще се преобразува към" : "converted to"} {f.units[0].code}</span>
                      )}
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!allFilled || loading}
            data-testid="phenoage-calculate-btn"
            className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white ${
              allFilled && !loading ? "bg-violet-600 hover:bg-violet-700" : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {loading ? (bg ? "Изчисляване…" : "Calculating…") : (bg ? "Изчисли биологична възраст" : "Calculate biological age")}
          </button>

          {history.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              <BioAgeTrendChart refreshKey={history.length} />
              <div>
                <div className="text-[11px] font-semibold text-gray-700 mb-2">{bg ? "Последни изчисления" : "Recent calculations"}</div>
                <ul className="divide-y divide-gray-100 rounded-md border border-gray-100">
                  {history.map(h => (
                    <li key={h.id} className="flex items-center justify-between px-2.5 py-1.5 text-xs" data-testid={`phenoage-history-${h.id}`}>
                      <span className="text-gray-500">{h.created_at ? new Date(h.created_at).toLocaleDateString(bg ? "bg-BG" : "en-US") : "—"}</span>
                      <span className="flex items-center gap-2">
                        <span className="text-gray-700">{h.chronological_age} → <strong>{h.biological_age}</strong> {bg ? "год" : "yrs"}</span>
                        <span className={`font-semibold ${h.age_gap > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                          {h.age_gap > 0 ? "+" : ""}{h.age_gap}
                        </span>
                        <ChevronRight size={10} className="text-gray-300" />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
      <AIModuleNotice variant="medical-educational" />
    </CollapsiblePanel>
  );
}
